/**
 * Does the site's water add up?
 *
 * Ten charts answer ten questions and none answers this one: did what we took
 * in match what we used and treated? It is the question a water manager opens
 * with, and on this data it does not balance — groundwater consumption has
 * been running above groundwater withdrawal, which is not physically possible
 * over the same period.
 *
 * Every component is read from the same day. The summary cards each pick their
 * own most recent reporting day, so building a balance from them would compare
 * a source that reported this morning against a plant that last reported in
 * June and call the difference a discrepancy.
 */

/** KPIs the balance is built from, as the backend spells them. */
export const BALANCE_KPIS = {
  BOREWELL: 'Borewell',
  ADMIN_TOP: 'From Borewell to Admin',
  STP_INLET: 'STP (Untreated)',
  ETP_INLET: 'ETP (Untreated)',
  FINAL_TREATED: 'Final Treated (STP + ETP)',
};

/**
 * What to call a KPI on screen, where the backend's own name is not the word
 * the plant uses.
 *
 * "ETP (Untreated)" and "STP (Untreated)" are the two inlet meters — the water
 * arriving at each plant to be cleaned. Those names describe the water by what
 * has not happened to it rather than by where it is, which is not how anyone
 * asks after it.
 *
 * The KPI strings themselves do not change. They are what the request filters
 * on, and renaming one is how three charts came back empty last week.
 */
const DISPLAY_NAMES = {
  'ETP (Untreated)': 'ETP Inlet',
  'STP (Untreated)': 'STP Inlet',
};

/**
 * Names a KPI for a reader.
 * @param {string} kpi - KPI as the backend spells it.
 * @returns {string} The plant's word for it, or the KPI name when there is none.
 */
export const kpiLabel = (kpi) => DISPLAY_NAMES[kpi] || kpi;

/** Third-party water arrives under one KPI per supplier. */
export const TANKER_KPIS = ['Awanthi 200 ppm', 'Awanthi 50 ppm', 'Shibra'];

/** Every KPI the balance needs, for the single request that fetches them. */
export const BALANCE_KPI_LIST = [
  ...Object.values(BALANCE_KPIS),
  ...TANKER_KPIS,
];

/**
 * Groups widget rows into one entry per day.
 * @param {Array<object>} rows - Widget rows carrying `period_ts` and `kpi`.
 * @returns {Array<{periodTs: string, values: object}>} Days, newest first.
 */
const daysFrom = (rows = []) => {
  const byDay = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.period_ts) return;

    if (!byDay.has(row.period_ts)) byDay.set(row.period_ts, {});
    byDay.get(row.period_ts)[row.kpi] = Number(row.sum_value) || 0;
  });

  return Array.from(byDay.entries())
    .map(([periodTs, values]) => ({ periodTs, values }))
    .sort((a, b) => new Date(b.periodTs) - new Date(a.periodTs));
};

/**
 * Sums a set of KPIs for one day.
 * @param {object} values - That day's KPI values.
 * @param {Array<string>} kpis - KPIs to add up.
 * @returns {number} Their total, absent KPIs counting as nothing.
 */
const sumOf = (values, kpis) =>
  kpis.reduce((total, kpi) => total + (values[kpi] ?? 0), 0);

/**
 * The site's water balance for the most recent day it can be built from.
 *
 * A day qualifies only when both a source and the consumption meter reported,
 * because a balance missing either half is not a balance. Components that were
 * silent that day are named rather than counted as zero — a treatment plant
 * that sent nothing and one that did not report are different findings.
 *
 * Each source is kept apart from the other. Borewell water and tanker water
 * arrive by different means and are managed by different people, so a single
 * "withdrawn" figure is a number nobody owns; the same goes for what each is
 * consumed as.
 *
 * Tanker consumption is not metered. Nothing meters what leaves a tanker, so
 * what was delivered is taken as what was used — which is an assumption, and
 * `tankerConsumedIsAssumed` exists so the page can say so rather than present
 * it as a reading.
 *
 * @param {Array<object>} rows - Widget rows stacked by KPI.
 * @returns {object|null} The day's components and what they imply, or null
 *   when no day in the window carried both halves.
 */
export const waterBalance = (rows = []) => {
  const usable = daysFrom(rows).find(
    ({ values }) =>
      sumOf(values, [BALANCE_KPIS.BOREWELL, ...TANKER_KPIS]) > 0 &&
      (values[BALANCE_KPIS.ADMIN_TOP] ?? 0) > 0
  );

  if (!usable) return null;

  const { periodTs, values } = usable;

  const groundWithdrawn = values[BALANCE_KPIS.BOREWELL] ?? 0;
  const tankerWithdrawn = sumOf(values, TANKER_KPIS);
  const withdrawn = groundWithdrawn + tankerWithdrawn;
  const groundConsumed = values[BALANCE_KPIS.ADMIN_TOP] ?? 0;
  const tankerConsumed = tankerWithdrawn;
  const sentToTreatment = sumOf(values, [
    BALANCE_KPIS.STP_INLET,
    BALANCE_KPIS.ETP_INLET,
  ]);
  const treated = values[BALANCE_KPIS.FINAL_TREATED] ?? 0;

  const silent = BALANCE_KPI_LIST.filter((kpi) => values[kpi] === undefined);

  return {
    periodTs,
    groundWithdrawn,
    tankerWithdrawn,
    withdrawn,
    groundConsumed,
    tankerConsumed,
    tankerConsumedIsAssumed: true,
    sentToTreatment,
    treated,
    treatedShare: withdrawn ? (treated / withdrawn) * 100 : null,
    consumedExceedsSource: groundConsumed > groundWithdrawn,
    treatedExceedsWithdrawn: treated > withdrawn,
    silent,
    balances: groundConsumed <= groundWithdrawn && treated <= withdrawn,
  };
};
